import React, { useState, useEffect, useCallback } from "react";
import "./webReport.css";
import "./printStyles.css";
import MetaDataSection from "./metaDataSection";
import ItemSection from "./itemSection";
import CoverPage from "./coverPage";
import PrintHeader from "./PrintHeader";
import PrintFooter from "./PrintFooter";
import PrintTableOfContents from "./PrintTableOfContents";
import { calculateSticky, formattedDate } from "./helperFunctions";
import Item from "../../components/Item";
import ConcerningLevelBadge from "../../components/ConcerningLevelBadge";

const ReportContent = ({
  challenges,
  metaDataItems,
  issueItems,
  activeSection,
  foundSection,
  foundSubsection,
  activeIssueInView,
  isMobile,
}) => {
  const [isSticky, setIsSticky] = useState(false);
  const [showIssueTitleInStickyHeader, setShowIssueTitleInStickyHeader] =
    useState(false);
  const [pageNumbers, setPageNumbers] = useState({});

  // Extract CoverPage data from metaDataItems
  const {
    Agents: agents,
    Client: client,
    Company: company,
    "Profile Image": profileImage,
    Address: address,
    User: user,
    Disclaimer: disclaimer,
    "Project Information": projectInfo,
  } = metaDataItems;

  const companyName = company?.name;
  const jobAddress = address?.formatted_address;

  const calculatePageNumbers = useCallback(() => {
    // Approximate printable area height per page (A4 at 96dpi with margins)
    // A4 = 297mm = ~1123px, minus ~0.75in margins top+bottom = ~144px, minus header/footer ~70px
    const PAGE_HEIGHT = 909;
    // TOC is page 1, cover page is not counted
    const TOC_PAGES = 1;
    const OFFSET_PAGES = TOC_PAGES;

    const numbers = {};

    // Add measurement class for print-like layout
    document.body.classList.add("print-measuring");

    // Force reflow
    void document.body.offsetHeight;

    const sectionIds = [
      "inspection-scope-disclaimer",
      "definitions-glossary",
      "inspection-overview",
      "challenge-summary-section",
      "property-details",
      ...(issueItems || []).map(
        (s) => `section-${s.name?.toLowerCase().replace(/\s+/g, "-")}`
      ),
    ];

    // Each section with page-break-before: always starts a new page.
    // We track cumulative page count.
    let currentPage = OFFSET_PAGES + 1;

    sectionIds.forEach((id) => {
      const el = document.getElementById(id);
      if (!el) return;

      // Since each section starts on a new page,
      // the page number is simply the current page counter
      numbers[id] = currentPage;

      // Calculate how many pages this section uses
      const sectionHeight = el.getBoundingClientRect().height;
      const pagesUsed = Math.max(1, Math.ceil(sectionHeight / PAGE_HEIGHT));
      currentPage += pagesUsed;
    });

    // Remove measurement class
    document.body.classList.remove("print-measuring");

    return numbers;
  }, [issueItems]);

  // Scroll listener and print preparation
  useEffect(() => {
    const handleScroll = () => {
      const activeSectionEl = document.getElementById(activeSection);

      if (!activeSectionEl) return;

      let showSticky = false;

      if (["property-details", "inspection-overview"].includes(activeSection)) {
        const titleSelector =
          activeSection === "property-details" ? ".title-wrapper" : ".item";
        showSticky = calculateSticky(isMobile, activeSectionEl, titleSelector);
        setShowIssueTitleInStickyHeader(false);
      } else {
        showSticky = calculateSticky(
          isMobile,
          activeSectionEl,
          ".title-wrapper",
          ".subtitle"
        );

        const activeSectionTitleRect = activeSectionEl
          .querySelector(".item")
          ?.getBoundingClientRect();

        const passedHeight = isMobile ? 24 : -44;
        if (activeSectionTitleRect?.top <= passedHeight) {
          setShowIssueTitleInStickyHeader(true);
        } else {
          setShowIssueTitleInStickyHeader(false);
        }
      }

      setIsSticky(showSticky);
    };

    const handleBeforePrint = () => {
      setIsSticky(false);
    };

    const handlePreparePrint = () => {
      const numbers = calculatePageNumbers();
      setPageNumbers(numbers);
    };

    window.addEventListener("scroll", handleScroll);
    window.addEventListener("beforeprint", handleBeforePrint);
    window.addEventListener("preparePrintReport", handlePreparePrint);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("beforeprint", handleBeforePrint);
      window.removeEventListener("preparePrintReport", handlePreparePrint);
    };
  }, [activeSection, calculatePageNumbers, isMobile]);

  // Trigger window.print() after page numbers are calculated and rendered
  useEffect(() => {
    if (Object.keys(pageNumbers).length > 0) {
      // Allow React to re-render the TOC with page numbers before printing
      const timeout = setTimeout(() => {
        window.print();
      }, 100);
      return () => clearTimeout(timeout);
    }
  }, [pageNumbers]);

  return (
    <div>
      <div
        className={`sticky-header-wrapper header ${isSticky ? "sticky" : ""}`}
      >
        <div>
          {foundSection?.name}
          {foundSubsection ? " - " : ""}
          {foundSubsection?.name}
          {showIssueTitleInStickyHeader && activeIssueInView && (
            <Item
              text={activeIssueInView.name || activeIssueInView.id}
              children={null}
              showBadge
              badgeContent={
                <ConcerningLevelBadge
                  concerningLevel={activeIssueInView.concern_level || 0}
                  itemType={activeIssueInView.type}
                />
              }
              showDivider={false}
            />
          )}
        </div>
      </div>
      <div className="main-content">
        {/* Cover page rendered outside table - no print header/footer */}
        <div className="metadata-container" id="section-cover-page">
          <CoverPage
            agents={agents}
            client={client}
            company={company}
            profileImage={profileImage}
            address={address}
            user={user}
            inspectedDate={formattedDate(projectInfo?.created_at)}
          />
        </div>

        {/* Fixed footer for print - position:fixed renders on every page */}
        <PrintFooter />

        {/* Table wrapper for print header repetition */}
        <table className="print-table-wrapper">
          <PrintHeader companyName={companyName} jobAddress={jobAddress} />
          <tbody>
            <tr>
              <td>
                <PrintTableOfContents
                  disclaimer={disclaimer}
                  issueItems={issueItems}
                  pageNumbers={pageNumbers}
                />
                <MetaDataSection
                  metaDataItems={metaDataItems}
                  challenges={challenges}
                />
                <ItemSection issueItems={issueItems} />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ReportContent;
